import { Button } from '@/global/components/Button'
import CalendarioEventos, {
    useCalendarRef,
} from '@/global/components/FullCalendar'
import { useThemeStore } from '@/global/components/ToggleTheme'
import { cn } from '@/lib/utils'
import { EventClickArg, EventContentArg } from '@fullcalendar/core/index.js'
import dayjs from 'dayjs'
import { useCompetencia } from 'global/components/Selects/SelectCompetencia'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { TabelaHorarioFuncionario } from 'service/api'
import { generateColor } from '../utils'
import { AdicionarTabelaHorarioFuncionario } from './modals/Adicionar'
import { EditarTabelaHorarioFuncionario } from './modals/ExcluirEditar'

interface IProps {
    value: ReturnType<typeof useCompetencia>[0]
    onChange: ReturnType<typeof useCompetencia>[1]
    tabelas?: TabelaHorarioFuncionario[]
    editable?: boolean
}

export default function CalendarioTabelasHorario({
    value: competencia,
    onChange,
    tabelas = [],
    editable = true,
}: IProps) {
    const [open, setOpen] = useState(false)
    const [tabelaEditar, setTabelaEditar] =
        useState<TabelaHorarioFuncionario | null>(null)
    const calendarRef = useCalendarRef()
    const mode = useThemeStore(state => state.theme)

    useEffect(() => {
        const calendarApi = (calendarRef.current as any)?.getApi()
        if (calendarApi)
            calendarApi.gotoDate(dayjs(competencia, 'YYYY-MM').toDate())
    }, [competencia])

    const events = tabelas.map((it, index) => {
        const { light, dark } = generateColor(
            `${it.tabelaHorarioId}-${it.TabelaHorario.nome}`,
        )

        const periodo = (() => {
            const start = dayjs.utc(it.dtInicial),
                end = it.dtFinal
                    ? dayjs.utc(it.dtFinal).add(1, 'day')
                    : dayjs.utc(competencia, 'YYYY-MM').add(2, 'month')

            if (start.isSame(end, 'day'))
                return {
                    date: start.format('YYYY-MM-DD'),
                }

            return {
                start: start.format('YYYY-MM-DD'),
                end: end?.format('YYYY-MM-DD'),
            }
        })()

        return {
            id: it.id?.toString() || index.toString(),
            title: it.TabelaHorario.nome,
            color: mode === 'dark' ? dark : light,
            textColor: mode === 'dark' ? 'white' : 'black',
            allDay: true,
            ...periodo,
        }
    })

    function handleEventClick(clickInfo: EventClickArg) {
        const tabela =
            tabelas.find(it => it.id.toString() === clickInfo.event.id) ?? null
        if (!tabela) return
        setTabelaEditar(tabela)
    }

    return [
        <div className="max-h-max size-full flex flex-col rounded-lg overflow-x-hidden overflow-y-visible border">
            <div className="w-full h-fit border rounded-t-lg border-b-0">
                <div className="inline-flex justify-between items-center overflow-x-auto gap-1 w-full p-2">
                    <div className="flex items-center gap-4">
                        <div
                            className={cn(
                                'flex flex-row items-center',
                                '*:rounded-none *:first:rounded-l-sm *:last:rounded-r-sm',
                                '*:h-10 *:aspect-1/1 *:p-2',
                            )}
                        >
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .subtract(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Mês anterior"
                                variant="outlined"
                                size="small"
                            >
                                <ChevronLeftIcon />
                            </Button>
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .add(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Próximo mês"
                                variant="outlined"
                                size="small"
                            >
                                <ChevronRightIcon />
                            </Button>
                        </div>
                        <span className="text-lg font-semibold truncate">
                            {dayjs(competencia).format('MMMM [de] YYYY')}
                        </span>
                    </div>
                    {editable && (
                        <Button
                            variant="contained"
                            onClick={() => setOpen(true)}
                            className="size-10"
                        >
                            <PlusIcon className="size-8 stroke-3" />
                        </Button>
                    )}
                </div>
            </div>
            <CalendarioEventos
                ref={calendarRef}
                events={events}
                eventContent={eventInfo =>
                    renderEventContent(eventInfo, tabelas)
                }
                eventOrder={(a: any, b: any) => {
                    const indexA = tabelas.findIndex(
                        it => it.id.toString() === a.id,
                    )
                    const indexB = tabelas.findIndex(
                        it => it.id.toString() === b.id,
                    )
                    return indexA - indexB
                }}
                eventClick={handleEventClick}
                moreLinkContent={renderMoreLinkContent}
            />
        </div>,
        <EditarTabelaHorarioFuncionario
            open={!!tabelaEditar}
            onClose={() => {
                setTabelaEditar(null)
            }}
            tabela={tabelaEditar!}
            disableFields={!editable}
        />,
        <AdicionarTabelaHorarioFuncionario
            onClose={() => setOpen(false)}
            open={open}
        />,
    ]
}

function renderMoreLinkContent(e: { num: number }) {
    return (
        <div
            className={cn(
                'inline-flex items-center justify-center max-w-full',
                'px-2 py-1 rounded-md bg-muted/40 text-muted-foreground',
                'text-xs font-medium hover:bg-muted/75 transition-colors',
            )}
            title={`Exibir mais ${e.num} tabela(s) de horário`}
        >
            +{e.num} ignorado(s)
        </div>
    )
}

function renderEventContent(
    eventInfo: EventContentArg,
    tabelas: TabelaHorarioFuncionario[] = [],
) {
    const isTrocaAutomatica = tabelas.find(
        it => it.id.toString() === eventInfo.event.id,
    )?.isTrocaAutomatica

    return (
        <div
            className={cn(
                'text-sm truncate px-2 min-w-full text-center cursor-pointer!',
                isTrocaAutomatica ? 'italic' : '',
            )}
            title={
                eventInfo.event.title +
                (isTrocaAutomatica ? ' (Troca automática)' : '')
            }
        >
            {eventInfo.event.title}
        </div>
    )
}
